import { useState, useEffect, useCallback, useRef, useContext } from "react";
import {
  Document,
  CreateDocumentData,
  UpdateDocumentData,
  UploadFileReference,
  GenerationSection,
  GenerationWebSocket,
  ExportResult,
  GenerationState,
  ThinkingEvent,
  StartExportJobResponse,
  ExportJobStatusResponse,
} from "../types/documentTypes";

import { useAuthFetch } from "../../../utils/useAuthFetch";
import { useUnsafeAuthProvider } from "../../../components/UnsafeAuthProvider";
import { useAuth0 } from "@auth0/auth0-react";
import { MsalContext } from "@azure/msal-react";
import {
  AccountInfo,
  InteractionRequiredAuthError,
  InteractionStatus,
} from "@azure/msal-browser";
import {
  getApiBaseUrl,
  getAuthenticationMethod,
} from "../../../config/runtimeConfig";

const EMPTY_ACCOUNTS: AccountInfo[] = [];
const MAX_EXPORT_JOB_TRACKING_MS = 60 * 60 * 1000;
const EXPORT_POLL_INTERVAL_MS = 3000;
const EXPORT_STATUS_POLL_TIMEOUT_MS = 10000;
const MAX_REGENERATE_JOB_TRACKING_MS = MAX_EXPORT_JOB_TRACKING_MS;
const REGENERATE_POLL_INTERVAL_MS = EXPORT_POLL_INTERVAL_MS;
const REGENERATE_STATUS_POLL_TIMEOUT_MS = EXPORT_STATUS_POLL_TIMEOUT_MS;
const MERIDIAN_GENERATION_AUTH_ACK_TIMEOUT_MS = 5500;
const MERIDIAN_GENERATION_AUTH_FAILURE_CLOSE_CODE = 4401;
const MERIDIAN_GENERATION_AUTH_ACK_TIMEOUT_CLOSE_CODE = 4408;
const MERIDIAN_GENERATION_PROTOCOL_ERROR_CLOSE_CODE = 1002;
const MERIDIAN_GENERATION_AUTH_ERROR_MESSAGE =
  "Unable to authenticate the generation connection. Please sign in again and retry.";
const MERIDIAN_GENERATION_PROTOCOL_ERROR_MESSAGE =
  "The generation service returned an invalid response. Please try again.";
export const MERIDIAN_GENERATION_TIMEOUT_ERROR_CODE =
  "meridian_generation_timeout";
export const MERIDIAN_GENERATION_TIMEOUT_MESSAGE =
  "Section generation timed out (>1200s). Section may be too large; try splitting or contact support.";

export type SectionReconciliationReason =
  | "reconnect"
  | "terminal-complete"
  | "terminal-error";

type RegenerateJobStatus = "pending" | "processing" | "completed" | "failed";

type StartRegenerateJobResponse = {
  message?: string;
  job_id: string;
  status: RegenerateJobStatus;
  poll_url?: string;
};

type RegenerateJobStatusResponse = {
  job_id: string;
  status: RegenerateJobStatus;
  document_id?: number;
  section_id?: number;
  created_at?: string;
  completed_at?: string | null;
  section?: Record<string, any>;
  code?: string;
  error_code?: string;
  error_message?: string;
  error?: string;
  detail?: unknown;
  message?: string;
};

export type MeridianGenerationError = {
  code: typeof MERIDIAN_GENERATION_TIMEOUT_ERROR_CODE | "generation_failed";
  message: string;
};

type PersistedExportJob = {
  jobId: string;
  documentId: number;
  startedAt: number;
  templateId?: number;
};

function getExportStorageKey(workspaceId: string): string {
  return `mm_export_jobs_${workspaceId}`;
}

function loadPersistedExportJobs(workspaceId: string): PersistedExportJob[] {
  try {
    const raw = localStorage.getItem(getExportStorageKey(workspaceId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function savePersistedExportJobs(
  workspaceId: string,
  jobs: PersistedExportJob[],
): void {
  try {
    if (jobs.length === 0) {
      localStorage.removeItem(getExportStorageKey(workspaceId));
    } else {
      localStorage.setItem(
        getExportStorageKey(workspaceId),
        JSON.stringify(jobs),
      );
    }
  } catch {
    // Ignore localStorage failures and continue without resume support.
  }
}

function upsertPersistedExportJob(
  workspaceId: string,
  job: PersistedExportJob,
): void {
  const jobs = loadPersistedExportJobs(workspaceId).filter(
    (existing) =>
      existing.documentId !== job.documentId && existing.jobId !== job.jobId,
  );
  savePersistedExportJobs(workspaceId, [...jobs, job]);
}

function removePersistedExportJob(workspaceId: string, jobId: string): void {
  const jobs = loadPersistedExportJobs(workspaceId);
  savePersistedExportJobs(
    workspaceId,
    jobs.filter((job) => job.jobId !== jobId),
  );
}

function cleanStalePersistedExportJobs(
  workspaceId: string,
): PersistedExportJob[] {
  const now = Date.now();
  const jobs = loadPersistedExportJobs(workspaceId);
  const freshJobs = jobs.filter(
    (job) => now - job.startedAt < MAX_EXPORT_JOB_TRACKING_MS,
  );
  savePersistedExportJobs(workspaceId, freshJobs);
  return freshJobs;
}

function getPersistedExportJob(
  workspaceId: string,
  documentId: number,
): PersistedExportJob | undefined {
  return cleanStalePersistedExportJobs(workspaceId)
    .filter((job) => job.documentId === documentId)
    .sort((a, b) => b.startedAt - a.startedAt)[0];
}

function normalizeExportDownloadUrl(downloadUrl: string): string {
  return downloadUrl.replace(/^.*?\/exports\//, "/exports/");
}

export function resolveRegeneratePollUrl(
  pollUrl: string | undefined,
  jobId: string,
  workspaceId: string,
): string {
  const base =
    pollUrl ||
    `/api/workspace/meridian/documents/sections/regenerate-jobs/${jobId}/status`;
  // The status endpoint's permission dependency requires workspace_id as a
  // query param (no {workspace_id} in the route path); polling without it
  // 422s on every attempt. Append it when the URL doesn't carry it already.
  const urlObj = new URL(base, window.location.origin);
  if (!urlObj.searchParams.has("workspace_id")) {
    urlObj.searchParams.append("workspace_id", workspaceId);
  }
  return urlObj.pathname + urlObj.search;
}

function readStringField(value: unknown, keys: string[]): string | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;

  for (const key of keys) {
    const field = record[key];
    if (typeof field === "string" && field.trim()) {
      return field;
    }
  }

  return null;
}

function extractErrorCode(value: unknown): string | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;

  return (
    readStringField(record, ["error_code", "errorCode", "code"]) ||
    extractErrorCode(record.detail) ||
    extractErrorCode(record.response) ||
    extractErrorCode(record.data)
  );
}

function extractErrorMessage(value: unknown): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.message;
  if (typeof value !== "object") return String(value);

  const record = value as Record<string, unknown>;
  const directMessage = readStringField(record, [
    "error_message",
    "error",
    "message",
    "detail",
  ]);
  if (directMessage) return directMessage;

  return (
    extractErrorMessage(record.detail) ||
    extractErrorMessage(record.response) ||
    extractErrorMessage(record.data)
  );
}

function isMeridianGenerationTimeout(code: string | null, message: string) {
  if (
    code === MERIDIAN_GENERATION_TIMEOUT_ERROR_CODE ||
    code === "generation_timeout" ||
    code === "section_generation_timeout"
  ) {
    return true;
  }

  const normalizedMessage = message.toLowerCase();
  return (
    normalizedMessage.includes("meridian_agent_timeout") ||
    normalizedMessage.includes("asyncio.timeouterror") ||
    normalizedMessage.includes("document_generator_service.py") ||
    normalizedMessage.includes("section generation timed out") ||
    (normalizedMessage.includes("generation") &&
      normalizedMessage.includes("timed out") &&
      normalizedMessage.includes("1200"))
  );
}

export function mapMeridianGenerationError(
  rawError: unknown,
): MeridianGenerationError {
  const code = extractErrorCode(rawError);
  const message = extractErrorMessage(rawError);

  if (isMeridianGenerationTimeout(code, message)) {
    return {
      code: MERIDIAN_GENERATION_TIMEOUT_ERROR_CODE,
      message: MERIDIAN_GENERATION_TIMEOUT_MESSAGE,
    };
  }

  return {
    code: "generation_failed",
    message: message || "Generation failed",
  };
}

export function mapRegeneratedSectionPayload(
  statusResponse: RegenerateJobStatusResponse,
) {
  const section = statusResponse.section;
  if (!section) {
    throw new Error("Regeneration completed without section content");
  }

  return {
    section_id: section.section_id,
    title: section.title,
    content: section.content,
    prompt: section.prompt,
    modifiedDate: section.modifiedDate ?? section.modified_date,
    level: section.level,
    status: section.status,
    order_index: section.order_index,
  };
}

export function mapPersistedDocumentSectionPayload(
  section: Record<string, any>,
): GenerationSection {
  return {
    section_id: section.section_id,
    title: section.title,
    content: section.content,
    prompt: section.prompt || "",
    level: section.level,
    status: "complete" as const,
    order_index: section.order_index,
    modified_date: section.modified_date,
    document_type_section_id: section.document_type_section_id,
    section_type: section.section_type,
    thinking_events: section.thinking_events,
    citations: section.citations,
    validation_error: section.validation_error,
  };
}

function getGenerationSectionKey(section: GenerationSection): string {
  const normalizedTitle = (section.title || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

  return section.document_type_section_id !== null &&
    section.document_type_section_id !== undefined
    ? `id:${section.document_type_section_id}`
    : `tl:${normalizedTitle}|${section.level ?? 0}`;
}

function getGenerationSectionIdentity(section: GenerationSection): string {
  return section.section_id !== null && section.section_id !== undefined
    ? `section:${section.section_id}`
    : getGenerationSectionKey(section);
}

function getGenerationSectionIdentityAliases(
  section: GenerationSection,
): string[] {
  const identity = getGenerationSectionIdentity(section);
  const aliases = [identity];

  if (
    section.document_type_section_id !== null &&
    section.document_type_section_id !== undefined
  ) {
    const documentTypeSectionIdentity = getGenerationSectionKey(section);
    if (documentTypeSectionIdentity !== identity) {
      aliases.push(documentTypeSectionIdentity);
    }
  }

  return aliases;
}

function getRenderedGenerationSections(
  state: GenerationState,
): GenerationSection[] {
  return [
    ...state.completedSections,
    ...(state.currentSection ? [state.currentSection] : []),
    ...state.pendingSections,
  ];
}

function completeLiveSection(section: GenerationSection): GenerationSection {
  return {
    ...section,
    status:
      section.status === "validating"
        ? ("validating" as const)
        : ("complete" as const),
  };
}

function mergePersistedSectionsWithRenderedState(
  previous: GenerationState,
  persistedSections: GenerationSection[],
  reason: SectionReconciliationReason,
): GenerationSection[] {
  const sectionsByIdentity = new Map<string, GenerationSection>();
  const canonicalIdentityByAlias = new Map<string, string>();
  const orderedIdentities: string[] = [];

  const upsert = (section: GenerationSection) => {
    const identityAliases = getGenerationSectionIdentityAliases(section);
    const identity =
      identityAliases
        .map((alias) => canonicalIdentityByAlias.get(alias))
        .find((alias): alias is string => Boolean(alias)) ?? identityAliases[0];

    if (!sectionsByIdentity.has(identity)) {
      orderedIdentities.push(identity);
    }

    sectionsByIdentity.set(identity, section);
    identityAliases.forEach((alias) =>
      canonicalIdentityByAlias.set(alias, identity),
    );
  };

  getRenderedGenerationSections(previous).forEach((section) => {
    upsert(
      reason === "terminal-complete" ? completeLiveSection(section) : section,
    );
  });
  persistedSections.forEach(upsert);

  return orderedIdentities
    .map((identity) => sectionsByIdentity.get(identity))
    .filter((section): section is GenerationSection => Boolean(section))
    .sort(
      (left, right) =>
        (left.order_index ?? Number.MAX_SAFE_INTEGER) -
        (right.order_index ?? Number.MAX_SAFE_INTEGER),
    );
}

export function reconcileGenerationStateWithPersistedSections(
  previous: GenerationState,
  persistedSections: GenerationSection[],
  reason: SectionReconciliationReason,
): Partial<GenerationState> {
  if (persistedSections.length === 0) {
    return {};
  }

  const completedSections =
    reason === "reconnect"
      ? persistedSections
      : mergePersistedSectionsWithRenderedState(
          previous,
          persistedSections,
          reason,
        );

  const completedSectionIds = new Set<number>(
    completedSections
      .map((section) => section.section_id)
      .filter(
        (sectionId): sectionId is number =>
          sectionId !== null && sectionId !== undefined,
      ),
  );
  const receivedSectionKeys = new Set(previous.receivedSectionKeys || []);

  completedSections.forEach((section) => {
    receivedSectionKeys.add(getGenerationSectionKey(section));
  });

  const reconciledState: Partial<GenerationState> = {
    completedSections,
    existingSectionIds: completedSectionIds,
    receivedSectionKeys,
    expectedSectionsCount:
      previous.expectedSectionsCount ??
      Math.max(persistedSections.length, completedSections.length),
  };

  if (reason === "reconnect") {
    const currentSectionPersisted =
      previous.currentSection &&
      completedSectionIds.has(previous.currentSection.section_id);
    const pendingSections = previous.pendingSections.filter(
      (section) => !completedSectionIds.has(section.section_id),
    );

    return {
      ...reconciledState,
      currentSection: currentSectionPersisted ? null : previous.currentSection,
      pendingSections,
      ...(currentSectionPersisted
        ? {
            isStreamComplete: true,
            isWaitingForNext: pendingSections.length > 0,
          }
        : {}),
    };
  }

  return {
    ...reconciledState,
    currentSection: null,
    pendingSections: [],
    isGenerating: false,
    isStreamComplete: true,
    isWaitingForNext: false,
    ...(reason === "terminal-complete" ? { isComplete: true } : {}),
  };
}

export const useFetchUserDocuments = (workspace_id: string) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();

  // Use a ref to store the latest authFetch to avoid recreating fetchDocuments
  const authFetchRef = useRef(authFetch);

  // Update ref when authFetch changes
  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const fetchDocuments = useCallback(async () => {
    if (!workspace_id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await authFetchRef.current(
        `/api/workspace/meridian/documents?workspace_id=${workspace_id}`,
        "fetch Meridian One documents.",
      );
      setDocuments(data);
      setError(null);
    } catch (err) {
      console.error("Documents fetch error:", err);
      const message =
        err instanceof Error ? err.message : "Failed to fetch documents";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [workspace_id]); // Only depend on workspace_id since authFetch is accessed via ref

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  return {
    documents,
    loading,
    error,
    refresh: fetchDocuments,
  };
};

export const useDocuments = () => {
  const [generationState, setGenerationState] = useState<GenerationState>({
    isGenerating: false,
    currentSection: null,
    pendingSections: [],
    completedSections: [],
    error: null,
    errorCode: null,
    isComplete: false,
    isStreamComplete: false,
    isWaitingForNext: false,
    exportState: "idle",
    downloadUrl: undefined,
    expectedSectionsCount: undefined,
    receivedSectionKeys: new Set<string>(),
    hasDynamicSections: false,
  });

  const connectionRef = useRef<(() => void) | null>(null);
  const isGeneratingRef = useRef(false);
  const reconcilePersistedSectionsRef = useRef<
    (
      documentId: number,
      workspaceId: string,
      reason: SectionReconciliationReason,
    ) => Promise<void>
  >(async () => undefined);
  const { unsafeUserName } = useUnsafeAuthProvider();
  const { getAccessTokenSilently } = useAuth0();

  const authenticationMethod = getAuthenticationMethod();
  const isAuth0Auth = authenticationMethod === "AUTH0";
  const isEntraIdAuth = authenticationMethod === "ENTRAID";
  const msalContext = useContext(MsalContext);
  const msalInstance = msalContext?.instance;
  const msalAccounts = msalContext?.accounts ?? EMPTY_ACCOUNTS;
  const msalInProgress = msalContext?.inProgress ?? InteractionStatus.None;

  const getEntraAccessToken = useCallback(async (): Promise<string | null> => {
    if (!isEntraIdAuth || !msalInstance) {
      return null;
    }

    const request = {
      scopes: [
        "api://0501f640-94a0-4fd9-a7ae-0e8fa5938130/access_as_user",
        "email",
        "openid",
        "profile",
      ],
      account: msalAccounts[0] ?? msalInstance.getActiveAccount() ?? undefined,
    };

    try {
      await msalInstance.initialize();
      const response = await msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        if (msalInProgress === InteractionStatus.None) {
          msalInstance.loginRedirect(request);
        }
      } else {
        console.error(
          "Failed to acquire EntraID token for WebSocket connection:",
          error,
        );
      }
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- msalContext excluded, only need core msal state
  }, [isEntraIdAuth, msalAccounts, msalInProgress, msalInstance]);

  const generateDocumentContent = async (
    documentId: number,
    workspace_id: string,
    {
      onSection,
      onComplete,
      onError,
      onReconnect,
    }: {
      onSection: (section: GenerationSection) => void;
      onComplete: () => void;
      onError: (error: string, errorCode?: string) => void;
      onReconnect?: () => void;
    },
    continueGeneration: boolean = false,
  ) => {
    const MAX_RECONNECT_ATTEMPTS = 5;
    const RECONNECT_DELAY_MS = 2000;
    let reconnectAttempts = 0;
    let currentSocket: GenerationWebSocket | null = null;
    let intentionallyClosed = false;

    const connect = async (asContinue: boolean) => {
      // Convert HTTP/HTTPS URL to WebSocket URL
      const apiUrl = getApiBaseUrl();
      const wsUrl = apiUrl.replace(
        /^https?:/,
        apiUrl.startsWith("https:") ? "wss:" : "ws:",
      );

      let token: string | null = null;
      try {
        if (isAuth0Auth) {
          token = await getAccessTokenSilently();
        } else if (isEntraIdAuth) {
          token = await getEntraAccessToken();
          if (!token) {
            console.error(
              "Unable to acquire EntraID access token for WebSocket connection.",
            );
            onError(
              "Unable to authenticate WebSocket connection. Please try again after logging in.",
            );
            return;
          }
        }
      } catch (tokenError) {
        console.error(
          "Failed to acquire auth token for WebSocket:",
          tokenError,
        );
        onError(
          "Authentication expired. Please refresh the page and try again.",
        );
        return;
      }

      if (!token) {
        onError(MERIDIAN_GENERATION_AUTH_ERROR_MESSAGE);
        return;
      }

      const params = new URLSearchParams({
        workspace_id,
        user_name: isEntraIdAuth
          ? (msalAccounts[0]?.username ?? unsafeUserName ?? "")
          : (unsafeUserName ?? ""),
        continue: String(asContinue),
      });

      const socket = new WebSocket(
        `${wsUrl}/api/workspace/meridian/documents/generate/${documentId}?${params.toString()}`,
      ) as GenerationWebSocket;
      currentSocket = socket;
      let authenticationAcknowledged = false;
      let authenticationAcknowledgementTimer: ReturnType<
        typeof setTimeout
      > | null = null;

      const clearAuthenticationAcknowledgementTimer = () => {
        if (authenticationAcknowledgementTimer !== null) {
          clearTimeout(authenticationAcknowledgementTimer);
          authenticationAcknowledgementTimer = null;
        }
      };

      socket.onopen = () => {
        if (intentionallyClosed || socket !== currentSocket) {
          socket.close(1000, "Generation connection no longer needed");
          return;
        }
        try {
          // Browser WebSockets cannot set an Authorization header. Authenticate
          // in the first frame so the bearer never appears in the URL, browser
          // history, access logs, or Referer headers.
          socket.send(JSON.stringify({ type: "auth", token }));
        } catch (error) {
          console.error("Failed to send WebSocket authentication frame:", error);
          socket.close(
            MERIDIAN_GENERATION_AUTH_ACK_TIMEOUT_CLOSE_CODE,
            "Authentication frame could not be sent",
          );
          return;
        }

        authenticationAcknowledgementTimer = setTimeout(() => {
          if (
            !authenticationAcknowledged &&
            !intentionallyClosed &&
            socket.readyState === WebSocket.OPEN
          ) {
            socket.close(
              MERIDIAN_GENERATION_AUTH_ACK_TIMEOUT_CLOSE_CODE,
              "Authentication acknowledgement timed out",
            );
          }
        }, MERIDIAN_GENERATION_AUTH_ACK_TIMEOUT_MS);
      };

      socket.onmessage = (event) => {
        let parsed: unknown;
        try {
          parsed = JSON.parse(event.data);
        } catch {
          socket.close(
            MERIDIAN_GENERATION_PROTOCOL_ERROR_CLOSE_CODE,
            "Invalid generation message",
          );
          return;
        }

        if (
          parsed === null ||
          typeof parsed !== "object" ||
          Array.isArray(parsed)
        ) {
          socket.close(
            MERIDIAN_GENERATION_PROTOCOL_ERROR_CLOSE_CODE,
            "Invalid generation message",
          );
          return;
        }
        const data = parsed as Record<string, any>;

        if (!authenticationAcknowledged) {
          if (
            data.status !== "authenticated" ||
            Object.keys(data).length !== 1
          ) {
            clearAuthenticationAcknowledgementTimer();
            socket.close(
              MERIDIAN_GENERATION_PROTOCOL_ERROR_CLOSE_CODE,
              "Authentication acknowledgement required",
            );
            return;
          }

          authenticationAcknowledged = true;
          clearAuthenticationAcknowledgementTimer();
          reconnectAttempts = 0;
          if (asContinue) {
            onReconnect?.();
          }
          return;
        }

        if (data.status === "authenticated") {
          socket.close(
            MERIDIAN_GENERATION_PROTOCOL_ERROR_CLOSE_CODE,
            "Unexpected authentication acknowledgement",
          );
          return;
        }

        if (data.status === "ping") {
          return; // Keepalive ping from server, ignore
        }
        if (data.status === "error") {
          // Log the raw server error for debugging, but only surface
          // user-safe, non-sensitive messages in the UI.
          console.error("Generation error from server:", data.error);
          // Check for token limit error
          if (data.error && data.error.includes("token count")) {
            const match = data.error.match(
              /token count \((\d+)\) exceeds .* allowed \((\d+)\)/,
            );
            if (match) {
              const [, currentTokens, maxTokens] = match;
              onError(
                `Token limit exceeded: Your document sources contain ${currentTokens} tokens, ` +
                  `but the maximum allowed is ${maxTokens} tokens. ` +
                  `Please reduce the size or number of source documents and try again.`,
              );
            } else {
              onError(
                "Generation failed due to a token limit. Please reduce the " +
                  "size or number of source documents and try again.",
              );
            }
          } else {
            const mappedError = mapMeridianGenerationError(data);
            onError(
              mappedError.code === MERIDIAN_GENERATION_TIMEOUT_ERROR_CODE
                ? mappedError.message
                : "Generation failed. Please try again, and if the problem " +
                    "persists contact support.",
              mappedError.code,
            );
          }
          return;
        }
        if (data.status === "complete" && !data.section_id) {
          // Final generation complete (no section_id).
          // Section-level "complete" messages (with section_id) are citation
          // validation updates and should fall through to onSection.
          socket.isComplete = true;
          onComplete();
          return;
        }
        if (data.status === "rate_limit") {
          onError(
            `Rate limit exceeded. Please try again in ${data.retryAfter} seconds.`,
          );
          return;
        }
        onSection(data as unknown as GenerationSection);
      };

      socket.onerror = (event) => {
        console.error("WebSocket error:", event);
      };

      socket.onclose = (event) => {
        clearAuthenticationAcknowledgementTimer();
        if (intentionallyClosed || socket.isComplete) {
          return;
        }
        if (event.code === MERIDIAN_GENERATION_AUTH_FAILURE_CLOSE_CODE) {
          onError(MERIDIAN_GENERATION_AUTH_ERROR_MESSAGE);
          return;
        }
        if (event.code === MERIDIAN_GENERATION_PROTOCOL_ERROR_CLOSE_CODE) {
          onError(MERIDIAN_GENERATION_PROTOCOL_ERROR_MESSAGE);
          return;
        }
        // Normal closure (1000) with no complete message means generation finished
        // on server side -- treat as unexpected drop
        if (event.code === 1001) {
          return; // Navigating away, don't reconnect
        }
        // Attempt reconnect for unexpected closures
        if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts++;
          const delay = RECONNECT_DELAY_MS * reconnectAttempts;
          console.warn(
            `WebSocket closed unexpectedly (code=${event.code}). ` +
              `Reconnecting in ${delay}ms (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})...`,
          );
          setTimeout(() => {
            if (!intentionallyClosed) {
              connect(true); // Always reconnect with continue=true
            }
          }, delay);
        } else {
          onError(
            "Connection lost after multiple reconnect attempts. " +
              "The generation may still be running on the server. " +
              "Try refreshing the page to reconnect.",
          );
        }
      };
    };

    await connect(continueGeneration);

    return () => {
      intentionallyClosed = true;
      if (
        currentSocket &&
        (currentSocket.readyState === WebSocket.CONNECTING ||
          currentSocket.readyState === WebSocket.OPEN)
      ) {
        currentSocket.close();
      }
    };
  };

  const startGeneration = useCallback(
    async (
      documentId: number,
      workspace_id: string,
      continueGeneration: boolean = false,
      onTerminalComplete?: (completedDocumentId: number) => void,
    ) => {
      if (isGeneratingRef.current) {
        return;
      }

      isGeneratingRef.current = true;

      if (connectionRef.current) {
        connectionRef.current();
        connectionRef.current = null;
      }

      setGenerationState((prev) => ({
        ...prev,
        currentSection: null,
        pendingSections: [],
        completedSections: continueGeneration ? prev.completedSections : [],
        error: null,
        isComplete: false,
        isGenerating: true,
        isStreamComplete: false,
        isWaitingForNext: false,
        // Reset expected counters for a new generation unless continuing
        expectedSectionsCount: continueGeneration
          ? prev.expectedSectionsCount
          : undefined,
        receivedSectionKeys: continueGeneration
          ? new Set(prev.receivedSectionKeys)
          : new Set<string>(),
        errorCode: null,
      }));

      const cleanup = generateDocumentContent(
        documentId,
        workspace_id,
        {
          onSection: (section) => {
            setGenerationState((prev) => {
              // Check if this is a thinking section event (has type field)
              const thinkingEventType = (section as any).type as
                | string
                | undefined;
              const isThinkingEvent =
                section.section_type === "Thinking" && thinkingEventType;

              // Handle thinking section events
              if (isThinkingEvent) {
                const thinkingEvent: ThinkingEvent = {
                  type: thinkingEventType as ThinkingEvent["type"],
                  content: (section as any).content,
                  tool: (section as any).tool,
                  description: (section as any).description,
                  args: (section as any).args,
                  result: (section as any).result,
                  log: (section as any).log,
                };

                // Find or create the thinking section
                const sectionKey = section.document_type_section_id
                  ? `id:${section.document_type_section_id}`
                  : `tl:${section.title}|${section.level ?? 0}`;

                // Check if we have an existing thinking section being built
                const existingInCurrent =
                  prev.currentSection?.section_type === "Thinking" &&
                  (prev.currentSection?.document_type_section_id ===
                    section.document_type_section_id ||
                    prev.currentSection?.title === section.title);

                if (thinkingEventType === "complete") {
                  // Check if currentSection is still this thinking section, or if the
                  // transition effect has already promoted it to completedSections and
                  // replaced currentSection with the next standard section.
                  const isCurrentStillThinking =
                    prev.currentSection?.section_type === "Thinking" &&
                    (prev.currentSection?.document_type_section_id ===
                      section.document_type_section_id ||
                      prev.currentSection?.title === section.title);

                  if (!isCurrentStillThinking) {
                    // The transition effect already moved the thinking section to
                    // completedSections. Update it in-place with the final event and
                    // leave currentSection (the next standard section) untouched.
                    const updatedCompleted = prev.completedSections.map((s) =>
                      s.section_type === "Thinking" &&
                      (s.document_type_section_id ===
                        section.document_type_section_id ||
                        s.title === section.title)
                        ? {
                            ...s,
                            status: "complete" as const,
                            content: thinkingEvent.content || s.content,
                            thinking_events: [
                              ...(s.thinking_events || []),
                              thinkingEvent,
                            ],
                          }
                        : s,
                    );
                    return {
                      ...prev,
                      completedSections: updatedCompleted,
                      receivedSectionKeys: new Set([
                        ...(prev.receivedSectionKeys || []),
                        sectionKey,
                      ]),
                    };
                  }

                  // Normal path: thinking section is still currentSection.
                  const incomingEvents = [
                    ...(prev.currentSection?.thinking_events || []),
                    thinkingEvent,
                  ];

                  // Check if a thinking section with this document_type_section_id
                  // already exists in completedSections from a prior cycle. If so,
                  // upsert (merge events) rather than appending a duplicate entry.
                  const existingCompletedIdx = prev.completedSections.findIndex(
                    (s) =>
                      s.section_type === "Thinking" &&
                      (s.document_type_section_id ===
                        section.document_type_section_id ||
                        s.title === section.title),
                  );

                  // Promote the next pending section directly — the typewriter does not
                  // fire for Thinking sections, so isStreamComplete won't be set by it.
                  const nextCurrent = prev.pendingSections[0] ?? null;
                  const remainingPending = prev.pendingSections.slice(1);

                  if (existingCompletedIdx !== -1) {
                    const updatedCompleted = prev.completedSections.map(
                      (s, i) =>
                        i === existingCompletedIdx
                          ? {
                              ...s,
                              status: "complete" as const,
                              content: thinkingEvent.content || s.content,
                              thinking_events: [
                                ...(s.thinking_events || []),
                                ...incomingEvents,
                              ],
                            }
                          : s,
                    );
                    return {
                      ...prev,
                      currentSection: nextCurrent,
                      pendingSections: remainingPending,
                      completedSections: updatedCompleted,
                      isStreamComplete: false,
                      isWaitingForNext: false,
                      receivedSectionKeys: new Set([
                        ...(prev.receivedSectionKeys || []),
                        sectionKey,
                      ]),
                    };
                  }

                  const finalSection: GenerationSection = {
                    section_id:
                      section.section_id ||
                      prev.currentSection?.section_id ||
                      Date.now(),
                    title: section.title,
                    content: thinkingEvent.content || "",
                    status: "complete" as const,
                    level: section.level,
                    order_index: section.order_index,
                    document_type_section_id: section.document_type_section_id,
                    section_type: "Thinking",
                    thinking_events: incomingEvents,
                  };

                  return {
                    ...prev,
                    currentSection: nextCurrent,
                    pendingSections: remainingPending,
                    completedSections: [
                      ...prev.completedSections,
                      finalSection,
                    ],
                    isStreamComplete: false,
                    isWaitingForNext: false,
                    receivedSectionKeys: new Set([
                      ...(prev.receivedSectionKeys || []),
                      sectionKey,
                    ]),
                  };
                } else {
                  // Accumulate the thinking event
                  if (existingInCurrent && prev.currentSection) {
                    // Add event to existing section
                    return {
                      ...prev,
                      currentSection: {
                        ...prev.currentSection,
                        thinking_events: [
                          ...(prev.currentSection.thinking_events || []),
                          thinkingEvent,
                        ],
                      },
                    };
                  } else {
                    // Create new thinking section placeholder
                    const newThinkingSection: GenerationSection = {
                      section_id: section.section_id || Date.now(),
                      title: section.title,
                      content: "",
                      status: "generating" as const,
                      level: section.level,
                      order_index: section.order_index,
                      document_type_section_id:
                        section.document_type_section_id,
                      section_type: "Thinking",
                      thinking_events: [thinkingEvent],
                    };

                    // If we have a current section, move it to pending first
                    if (prev.currentSection) {
                      return {
                        ...prev,
                        currentSection: newThinkingSection,
                        pendingSections: [
                          ...prev.pendingSections,
                          prev.currentSection,
                        ],
                        isStreamComplete: false,
                        isWaitingForNext: false,
                      };
                    }

                    return {
                      ...prev,
                      currentSection: newThinkingSection,
                    };
                  }
                }
              }

              // Standard section handling (non-thinking sections)
              // Check if this section already exists to prevent duplicates
              const normalizeTitle = (s?: string) =>
                (s || "").trim().toLowerCase().replace(/\s+/g, " ");
              const keyOf = (s?: GenerationSection | null) =>
                s ? `${normalizeTitle(s.title)}|${s.level ?? 0}` : "";
              const incomingKey = `${normalizeTitle(section.title)}|${section.level ?? 0}`;

              const isExistingById =
                prev.existingSectionIds?.has(section.section_id) ||
                prev.completedSections.some(
                  (s) => s.section_id === section.section_id,
                ) ||
                prev.currentSection?.section_id === section.section_id ||
                prev.pendingSections.some(
                  (s) => s.section_id === section.section_id,
                );

              const matchesKey = (s?: GenerationSection | null) =>
                !!s && keyOf(s) === incomingKey;
              const isExistingByKeyAndContent =
                (matchesKey(prev.currentSection) &&
                  prev.currentSection?.content === section.content) ||
                prev.pendingSections.some(
                  (s) =>
                    `${normalizeTitle(s.title)}|${s.level ?? 0}` ===
                      incomingKey && s.content === section.content,
                ) ||
                prev.completedSections.some(
                  (s) =>
                    `${normalizeTitle(s.title)}|${s.level ?? 0}` ===
                      incomingKey && s.content === section.content,
                );

              // Handle citation validation update: same section_id, status "complete",
              // potentially updated content and citations (Message 2 of citation verification flow)
              if (
                isExistingById &&
                section.status === "complete" &&
                ((section as any).citations ||
                  (section as any).validation_error)
              ) {
                const updateSection = (s: GenerationSection) => {
                  if (s.section_id !== section.section_id) return s;
                  return {
                    ...s,
                    status: "complete" as const,
                    content: section.content || s.content,
                    citations: (section as any).citations || s.citations,
                    validation_error: (section as any).validation_error,
                  };
                };
                return {
                  ...prev,
                  currentSection: prev.currentSection
                    ? updateSection(prev.currentSection)
                    : null,
                  completedSections: prev.completedSections.map(updateSection),
                };
              }

              if (isExistingById || isExistingByKeyAndContent) {
                return prev; // Don't add duplicate sections
              }

              // Special case: if we're waiting for the next section but receive a section with the
              // same key as the current section (common duplicate of the last section), ignore it.
              const currentKey = keyOf(prev.currentSection);
              if (
                incomingKey === currentKey &&
                prev.isStreamComplete &&
                prev.pendingSections.length === 0
              ) {
                return prev;
              }

              // Compute a stable key: typeSectionId if available else (title|level)
              const stableKey =
                section.document_type_section_id !== null &&
                section.document_type_section_id !== undefined
                  ? `id:${section.document_type_section_id}`
                  : `tl:${normalizeTitle(section.title)}|${section.level ?? 0}`;

              // Stop accepting more sections if we've already received expected count
              const expected = prev.expectedSectionsCount;
              const receivedUniqueCount = prev.receivedSectionKeys?.size ?? 0;
              if (expected !== undefined && receivedUniqueCount >= expected) {
                return prev;
              }

              // If no current section yet, start with this one
              if (!prev.currentSection) {
                return {
                  ...prev,
                  currentSection: section,
                  isStreamComplete: true,
                  receivedSectionKeys: new Set([
                    ...(prev.receivedSectionKeys || []),
                    stableKey,
                  ]),
                };
              }

              // Always queue incoming sections; the view will promote when typing finishes
              return {
                ...prev,
                pendingSections: [...prev.pendingSections, section],
                receivedSectionKeys: new Set([
                  ...(prev.receivedSectionKeys || []),
                  stableKey,
                ]),
              };
            });
          },
          onComplete: () => {
            isGeneratingRef.current = false;
            setGenerationState((prev) => ({
              ...prev,
              isComplete: true,
              isGenerating: false,
              expectedSectionsCount:
                prev.expectedSectionsCount ??
                (prev.receivedSectionKeys?.size || undefined),
              // Do not clear currentSection here; allow UI typewriter to finish and finalize
            }));
            void reconcilePersistedSectionsRef.current(
              documentId,
              workspace_id,
              "terminal-complete",
            );
            onTerminalComplete?.(documentId);
          },
          onError: (error, errorCode) => {
            isGeneratingRef.current = false;
            setGenerationState((prev) => ({
              ...prev,
              error,
              errorCode,
              isGenerating: false,
            }));
            void reconcilePersistedSectionsRef.current(
              documentId,
              workspace_id,
              "terminal-error",
            );
          },
          onReconnect: () => {
            void reconcilePersistedSectionsRef.current(
              documentId,
              workspace_id,
              "reconnect",
            );
          },
        },
        continueGeneration,
      );

      try {
        connectionRef.current = await cleanup;
      } catch (err) {
        // If the connection never gets established (e.g. auth/token failure),
        // clear the in-flight flag so the guard above doesn't permanently
        // block future Generate/Reconnect attempts and leave the UI stuck.
        console.error("Failed to start generation stream:", err);
        isGeneratingRef.current = false;
        setGenerationState((prev) => ({
          ...prev,
          isGenerating: false,
          error:
            "Couldn't connect to the generation service. Please try again.",
        }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- generateDocumentContent recreated on render but behavior is stable
    [],
  );

  const stopGeneration = useCallback(() => {
    if (connectionRef.current) {
      connectionRef.current();
      connectionRef.current = null;
    }
    setGenerationState((prev) => ({
      ...prev,
      isGenerating: false,
    }));
  }, []);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();
  const authFetchBlob = useAuthFetch("blob");
  const isMountedRef = useRef(true);
  const activeExportJobIdRef = useRef<string | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      activeExportJobIdRef.current = null;
    };
  }, []);

  const updateGenerationState = useCallback(
    (
      updates:
        | Partial<GenerationState>
        | ((prev: GenerationState) => Partial<GenerationState>),
    ) => {
      setGenerationState((prev) => ({
        ...prev,
        ...(typeof updates === "function" ? updates(prev) : updates),
      }));
    },
    [],
  );

  const createDocument = useCallback(
    async (data: CreateDocumentData) => {
      setLoading(true);
      try {
        // Map to backend's expected format
        const mappedData = {
          title: data.title,
          document_type_id: data.documentTypeId, // Convert to snake_case
          inputs: data.inputs,
          sourceDocuments: data.sourceDocuments.map(
            (doc: { fileReference: UploadFileReference }) => ({
              fileReference: {
                ...doc.fileReference,
                sourceFieldId: doc.fileReference.sourceFieldId,
              },
            }),
          ),
          // Remove workspace_id from body as it's in the URL parameter
        };

        const response = await authFetch(
          `/api/workspace/meridian/documents/createDocument?workspace_id=${data.workspace_id}`,
          "create document",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(mappedData),
          },
        );
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to create document";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  const updateDocument = useCallback(
    async (data: UpdateDocumentData) => {
      setLoading(true);
      try {
        const sourceDocuments = Array.isArray(data.sourceDocuments)
          ? data.sourceDocuments
          : [];

        const mappedData = {
          title: data.title,
          documentTypeId: data.documentTypeId,
          inputs: data.inputs || {},
          workspace_id: data.workspace_id,
          sourceDocuments: sourceDocuments
            .filter(
              (doc) => doc && doc.fileReference && doc.fileReference.isNew,
            )
            .map((doc) => ({
              fileReference: {
                originalName: doc.fileReference.originalName,
                documentType: doc.fileReference.documentType,
                uploadDate: doc.fileReference.uploadDate,
                fileSize: doc.fileReference.fileSize,
                mimeType: doc.fileReference.mimeType,
                filePath: doc.fileReference.filePath,
                sourceFieldId: doc.fileReference.sourceFieldId,
                isNew: doc.fileReference.isNew,
              },
            })),
        };

        const response = await authFetch(
          `/api/workspace/meridian/documents/${data.documentId}?workspace_id=${data.workspace_id}`,
          "update document",
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(mappedData),
          },
        );
        return response;
      } catch (err) {
        console.error("Document update error:", err);
        const message =
          err instanceof Error ? err.message : "Failed to update document";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  const getDocument = useCallback(
    async (documentId: number, workspace_id: string) => {
      setLoading(true);
      try {
        const response = await authFetch(
          `/api/workspace/meridian/documents/${documentId}?workspace_id=${workspace_id}`,
          "fetch document",
        );
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to fetch document";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  const deleteSourceDocument = useCallback(
    async (sourceDocumentId: number, workspace_id: string) => {
      if (!sourceDocumentId) {
        console.error("Invalid source document ID:", sourceDocumentId);
        return false;
      }

      setLoading(true);
      try {
        await authFetch(
          `/api/workspace/meridian/documents/source-documents/${sourceDocumentId}?workspace_id=${workspace_id}`,
          "delete source document",
          {
            method: "DELETE",
          },
        );
        return true;
      } catch (err: any) {
        if (err.response?.status === 404) {
          console.warn(
            "Document not found (already deleted):",
            sourceDocumentId,
          );
          return true;
        }
        const message =
          err.response?.data?.error ||
          (err instanceof Error
            ? err.message
            : "Failed to delete source document");
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  const deleteDocument = useCallback(
    async (documentId: number, workspace_id: string) => {
      if (!documentId) {
        console.error("Invalid document ID:", documentId);
        return false;
      }

      setLoading(true);
      try {
        await authFetch(
          `/api/workspace/meridian/documents/${documentId}?workspace_id=${workspace_id}`,
          "delete document",
          {
            method: "DELETE",
          },
        );
        return true;
      } catch (err: any) {
        if (err.response?.status === 404) {
          console.warn("Document not found (already deleted):", documentId);
          return true;
        }
        const message =
          err.response?.data?.detail ||
          err.response?.data?.error ||
          (err instanceof Error ? err.message : "Failed to delete document");
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  const exportDocument = useCallback(
    async (
      documentId: number,
      workspace_id: string,
      templateId?: number,
    ): Promise<ExportResult> => {
      const pollExportJobStatus = async (
        jobId: string,
      ): Promise<
        | ExportJobStatusResponse
        | { status: "cancelled"; error_message?: string }
      > => {
        const pollingDeadline = Date.now() + MAX_EXPORT_JOB_TRACKING_MS;
        let attempt = 0;

        while (Date.now() < pollingDeadline) {
          if (!isMountedRef.current) {
            return { status: "cancelled" };
          }

          attempt += 1;
          const remainingMs = pollingDeadline - Date.now();
          const requestTimeout = Math.min(
            EXPORT_STATUS_POLL_TIMEOUT_MS,
            remainingMs,
          );

          try {
            const statusResponse = (await authFetch(
              `/api/workspace/meridian/documents/export-jobs/${jobId}/status?workspace_id=${workspace_id}`,
              "fetch export job status",
              undefined,
              requestTimeout,
            )) as ExportJobStatusResponse;

            if (
              statusResponse.status === "completed" ||
              statusResponse.status === "failed"
            ) {
              return statusResponse;
            }
          } catch (err: any) {
            if (err?.status === 400 || err?.status === 404) {
              return {
                status: "failed",
                error_message:
                  err instanceof Error ? err.message : "Export job not found",
              } as ExportJobStatusResponse;
            }

            console.warn(
              `Export status poll attempt ${attempt} failed for job ${jobId}, retrying...`,
              err instanceof Error ? err.message : err,
            );
          }

          const remainingAfterPollMs = pollingDeadline - Date.now();
          if (remainingAfterPollMs <= 0) {
            break;
          }

          await new Promise((resolve) =>
            setTimeout(
              resolve,
              Math.min(EXPORT_POLL_INTERVAL_MS, remainingAfterPollMs),
            ),
          );
        }

        return {
          status: "failed",
          error_message: "Export timed out",
        } as ExportJobStatusResponse;
      };

      const trackExportJob = async (jobId: string) => {
        if (activeExportJobIdRef.current === jobId) {
          return;
        }

        activeExportJobIdRef.current = jobId;

        try {
          const jobResult = await pollExportJobStatus(jobId);

          if (
            !isMountedRef.current ||
            activeExportJobIdRef.current !== jobId ||
            jobResult.status === "cancelled"
          ) {
            return;
          }

          removePersistedExportJob(workspace_id, jobId);

          if (
            jobResult.status === "completed" &&
            jobResult.downloadUrl &&
            jobResult.export
          ) {
            updateGenerationState({
              exportState: "ready",
              downloadUrl: normalizeExportDownloadUrl(jobResult.downloadUrl),
              error: null,
            });
            return;
          }

          const message =
            jobResult.error_message || "Failed to export document";
          setError(message);
          updateGenerationState({
            exportState: "failed",
            error: message,
          });
        } finally {
          if (activeExportJobIdRef.current === jobId) {
            activeExportJobIdRef.current = null;
          }
        }
      };

      setLoading(true);
      try {
        updateGenerationState({
          exportState: "exporting",
          downloadUrl: undefined,
          error: null,
        });

        const response = (await authFetch(
          `/api/workspace/meridian/documents/${documentId}/export?workspace_id=${workspace_id}`,
          "export document",
          {
            method: "POST",
            body: JSON.stringify({ templateId }),
            headers: {
              "Content-Type": "application/json",
            },
          },
          60000,
        )) as StartExportJobResponse;

        if (!response.job_id) {
          throw new Error("Export did not return a job ID");
        }

        upsertPersistedExportJob(workspace_id, {
          jobId: response.job_id,
          documentId,
          startedAt: Date.now(),
          templateId,
        });

        void trackExportJob(response.job_id);

        return {
          success: true,
        };
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to export document";
        setError(message);
        updateGenerationState({
          exportState: "failed",
          error: message,
        });
        return {
          success: false,
          error: message,
        };
      } finally {
        setLoading(false);
      }
    },
    [authFetch, updateGenerationState],
  );

  const resumeExportJob = useCallback(
    async (documentId: number, workspace_id: string) => {
      const persistedJob = getPersistedExportJob(workspace_id, documentId);
      if (!persistedJob) {
        return;
      }

      if (activeExportJobIdRef.current === persistedJob.jobId) {
        return;
      }

      updateGenerationState({
        exportState: "exporting",
        downloadUrl: undefined,
        error: null,
      });

      const pollingDeadline = Date.now() + MAX_EXPORT_JOB_TRACKING_MS;
      activeExportJobIdRef.current = persistedJob.jobId;

      try {
        while (Date.now() < pollingDeadline) {
          if (!isMountedRef.current) {
            return;
          }

          try {
            const statusResponse = (await authFetch(
              `/api/workspace/meridian/documents/export-jobs/${persistedJob.jobId}/status?workspace_id=${workspace_id}`,
              "fetch export job status",
              undefined,
              EXPORT_STATUS_POLL_TIMEOUT_MS,
            )) as ExportJobStatusResponse;

            if (statusResponse.status === "completed") {
              removePersistedExportJob(workspace_id, persistedJob.jobId);
              updateGenerationState({
                exportState: "ready",
                downloadUrl: normalizeExportDownloadUrl(
                  statusResponse.downloadUrl || "",
                ),
                error: null,
              });
              return;
            }

            if (statusResponse.status === "failed") {
              removePersistedExportJob(workspace_id, persistedJob.jobId);
              const message =
                statusResponse.error_message || "Failed to export document";
              setError(message);
              updateGenerationState({
                exportState: "failed",
                error: message,
              });
              return;
            }
          } catch (err: any) {
            if (err?.status === 400 || err?.status === 404) {
              removePersistedExportJob(workspace_id, persistedJob.jobId);
              const message =
                err instanceof Error ? err.message : "Export job not found";
              setError(message);
              updateGenerationState({
                exportState: "failed",
                error: message,
              });
              return;
            }
          }

          await new Promise((resolve) =>
            setTimeout(resolve, EXPORT_POLL_INTERVAL_MS),
          );
        }

        removePersistedExportJob(workspace_id, persistedJob.jobId);
        const message = "Export timed out";
        setError(message);
        updateGenerationState({
          exportState: "failed",
          error: message,
        });
      } finally {
        if (activeExportJobIdRef.current === persistedJob.jobId) {
          activeExportJobIdRef.current = null;
        }
      }
    },
    [authFetch, updateGenerationState],
  );

  const downloadExport = useCallback(
    async (documentIdOrPath: string, workspace_id?: string): Promise<Blob> => {
      setLoading(true);
      try {
        let downloadUrl: string;
        // If given a path, construct the correct URL format
        // Path format is "YYYY/MM/DD/filename.docx" and we need to split it
        const pathParts = documentIdOrPath.split("/");

        const [year, month, day, filename] = pathParts;
        downloadUrl = `/api/workspace/meridian/documents/exports/${year}/${month}/${day}/${filename}?workspace_id=${workspace_id}`;

        const response = await authFetchBlob(downloadUrl, "download export", {
          method: "GET",
          headers: {
            Accept:
              "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          },
        });

        const blob = await response.blob();
        return blob;
      } catch (err) {
        console.error("Download export error:", err);
        const message =
          err instanceof Error ? err.message : "Failed to download export";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetchBlob],
  );

  const downloadFileToBrowser = async (
    exportPath: string,
    workspace_id: string,
  ) => {
    const blob = await downloadExport(exportPath, workspace_id);

    // Use the filename for the download
    const filename = exportPath.split("/").pop() || "document.docx";

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const regenerateSection = useCallback(
    async (
      documentId: number,
      sectionId: number,
      workspace_id: string,
      customPrompt?: string,
    ) => {
      const pollRegenerateJobStatus = async (
        jobId: string,
        pollUrl?: string,
      ): Promise<
        RegenerateJobStatusResponse | { status: "cancelled"; error?: string }
      > => {
        const statusUrl = resolveRegeneratePollUrl(pollUrl, jobId, workspace_id);
        const pollingDeadline = Date.now() + MAX_REGENERATE_JOB_TRACKING_MS;
        let attempt = 0;

        while (Date.now() < pollingDeadline) {
          if (!isMountedRef.current) {
            return { status: "cancelled", error: "Regeneration cancelled" };
          }

          attempt += 1;
          const remainingMs = pollingDeadline - Date.now();
          const requestTimeout = Math.min(
            REGENERATE_STATUS_POLL_TIMEOUT_MS,
            remainingMs,
          );

          try {
            const statusResponse = (await authFetch(
              statusUrl,
              "fetch section regeneration job status",
              undefined,
              requestTimeout,
            )) as RegenerateJobStatusResponse;

            if (
              statusResponse.status === "completed" ||
              statusResponse.status === "failed"
            ) {
              return statusResponse;
            }
          } catch (err: any) {
            if (err?.status === 400 || err?.status === 404) {
              return {
                status: "failed",
                error_message:
                  err instanceof Error
                    ? err.message
                    : "Regeneration job not found",
              } as RegenerateJobStatusResponse;
            }

            console.warn(
              `Regeneration status poll attempt ${attempt} failed for job ${jobId}, retrying...`,
              err instanceof Error ? err.message : err,
            );
          }

          const remainingAfterPollMs = pollingDeadline - Date.now();
          if (remainingAfterPollMs <= 0) {
            break;
          }

          await new Promise((resolve) =>
            setTimeout(
              resolve,
              Math.min(REGENERATE_POLL_INTERVAL_MS, remainingAfterPollMs),
            ),
          );
        }

        return {
          status: "failed",
          error_message: "Regeneration timed out",
        } as RegenerateJobStatusResponse;
      };

      try {
        const response = (await authFetch(
          `/api/workspace/meridian/documents/${documentId}/sections/${sectionId}/regenerate-async?workspace_id=${workspace_id}`,
          "start section regeneration",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              prompt: customPrompt,
            }),
          },
          60000,
        )) as StartRegenerateJobResponse;

        if (!response.job_id) {
          throw new Error("Regeneration did not return a job ID");
        }

        const jobResult = await pollRegenerateJobStatus(
          response.job_id,
          response.poll_url,
        );

        if (jobResult.status === "cancelled") {
          return {
            success: false,
            error: jobResult.error || "Regeneration cancelled",
          };
        }

        if (jobResult.status === "failed") {
          const mappedError = mapMeridianGenerationError(jobResult);
          return {
            success: false,
            error: mappedError.message,
            errorCode: mappedError.code,
          };
        }

        return {
          success: true,
          data: mapRegeneratedSectionPayload(jobResult),
        };
      } catch (err: any) {
        console.error("Regeneration error:", err);

        if (err.status === "rate_limit") {
          return {
            success: false,
            error: `Rate limit exceeded. Please wait ${Math.ceil(err.retryAfter)} seconds.`,
            isRateLimit: true,
            retryAfter: err.retryAfter,
          };
        }

        const mappedError = mapMeridianGenerationError(err);
        // Extract meaningful error message
        let errorMessage =
          mappedError.message || "Failed to regenerate section";

        // Handle different error response formats. Keep timeout failures
        // normalized so raw backend traces like asyncio.TimeoutError do not
        // leak into the UI.
        if (
          mappedError.code !== MERIDIAN_GENERATION_TIMEOUT_ERROR_CODE &&
          err.response
        ) {
          // HTTP error response
          if (err.response.data) {
            if (typeof err.response.data === "string") {
              errorMessage = err.response.data;
            } else if (err.response.data.detail) {
              errorMessage = err.response.data.detail;
            } else if (err.response.data.message) {
              errorMessage = err.response.data.message;
            }
          }
        } else if (err.message) {
          errorMessage = err.message;
        } else if (typeof err === "string") {
          errorMessage = err;
        }

        // Handle specific backend errors
        if (
          errorMessage.includes("invalid x-api-key") ||
          errorMessage.includes("authentication_error")
        ) {
          errorMessage =
            "AI service authentication error. Please contact your administrator to configure the API keys.";
        }

        return {
          success: false,
          error: errorMessage,
          errorCode: mappedError.code,
        };
      }
    },
    [authFetch],
  );

  const upgradeDocument = useCallback(
    async (documentId: number, workspace_id: string) => {
      setLoading(true);
      try {
        const response = await authFetch(
          `/api/workspace/meridian/documents/${documentId}/upgrade?workspace_id=${workspace_id}`,
          "upgrade document",
          {
            method: "POST",
          },
        );
        return response.document;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to upgrade document";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  const getDocumentSections = useCallback(
    async (
      documentId: number,
      workspace_id: string,
    ): Promise<GenerationSection[]> => {
      setLoading(true);
      try {
        const response = await authFetch(
          `/api/workspace/meridian/documents/${documentId}/sections?workspace_id=${workspace_id}`,
          "fetch document sections",
        );

        // The API returns an array directly, not wrapped in a sections property
        return response.map(mapPersistedDocumentSectionPayload);
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "Failed to fetch document sections";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  const reconcilePersistedSectionsFromDb = useCallback(
    async (
      documentId: number,
      workspace_id: string,
      reason: SectionReconciliationReason,
    ) => {
      try {
        const sections = await getDocumentSections(documentId, workspace_id);

        if (!isMountedRef.current || sections.length === 0) {
          return;
        }

        setGenerationState((prev) => ({
          ...prev,
          ...reconcileGenerationStateWithPersistedSections(
            prev,
            sections,
            reason,
          ),
        }));
      } catch (err) {
        console.warn("Failed to reconcile document sections from DB:", err);
      }
    },
    [getDocumentSections],
  );

  reconcilePersistedSectionsRef.current = reconcilePersistedSectionsFromDb;

  const updateSection = useCallback(
    async (
      documentId: number,
      sectionId: number,
      content: string,
      workspace_id: string,
    ) => {
      try {
        const response = await authFetch(
          `/api/workspace/meridian/documents/${documentId}/sections/${sectionId}?workspace_id=${workspace_id}`,
          "update section content",
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ content }),
          },
        );

        return {
          success: true,
          data: {
            section_id: response.section.section_id,
            title: response.section.title,
            content: response.section.content,
            modifiedDate: response.section.modifiedDate,
            status: response.section.status,
          },
        };
      } catch (err: any) {
        console.error("Update error:", err);
        return {
          success: false,
          error: err.message || "Failed to update section",
        };
      }
    },
    [authFetch],
  );

  const getSectionPrompt = useCallback(
    async (documentId: number, sectionId: number, workspace_id: string) => {
      try {
        const response = await authFetch(
          `/api/workspace/meridian/documents/${documentId}/sections/${sectionId}/prompt?workspace_id=${workspace_id}`,
          "fetch section prompt",
        );

        return {
          success: true,
          data: {
            prompt: response.prompt,
            context: {
              inputs: response.context.inputs,
              sources: {
                sources: response.context.sources.sources,
                examples: response.context.sources.examples,
                guidance: response.context.sources.guidance,
              },
            },
          },
        };
      } catch (err: any) {
        console.error("Error fetching section prompt:", err);
        return {
          success: false,
          error: err.message || "Failed to fetch section prompt",
        };
      }
    },
    [authFetch],
  );

  const preloadSourcePdfs = useCallback(
    async (documentId: number, workspaceId: string) => {
      try {
        await authFetch(
          `/api/workspace/meridian/documents/${documentId}/sources/preload-pdfs?workspace_id=${workspaceId}`,
          "preload source PDFs",
          { method: "POST" },
        );
      } catch {
        // Non-critical -- viewer will still work, just slower on first open
      }
    },
    [authFetch],
  );

  return {
    generationState,
    startGeneration,
    stopGeneration,
    updateGenerationState,
    loading,
    error,
    createDocument,
    generateDocumentContent,
    updateDocument,
    getDocument,
    deleteSourceDocument,
    deleteDocument,
    exportDocument,
    resumeExportJob,
    downloadExport,
    downloadFileToBrowser,
    regenerateSection,
    upgradeDocument,
    getDocumentSections,
    updateSection,
    getSectionPrompt,
    preloadSourcePdfs,
  };
};
